/**
 * Billing data layer — every money-moving call the SPA makes (coach subscription checkout /
 * plan change / portal, Stripe Connect onboarding, coach→client charges, client pay-now /
 * portal). All go through the global apiClient (CSRF, credentials, 429 handling, Sentry).
 *
 * Returns the non-throwing ApiResponse. Callers depend on that shape:
 *  - `url` on success (Stripe-hosted page to navigate to),
 *  - `code` on failure (CoachUpgradeHub falls back to checkout on SUBSCRIPTION_CANCELED),
 *  - `message` = the server's own text (or undefined), so each caller keeps its own fallback copy,
 *  - `status` for the cancel-charge "Server responded N" detail.
 * Writes are never retried by apiClient — important here: a retried checkout/charge could
 * create a duplicate Stripe session or invoice row.
 */

import { api, type ApiResponse } from '@/lib/apiClient';
// API-6: request bodies are typed by the handlers' own zod schemas (type-only import — nothing
// server-side is bundled). A shape the server would reject is a compile error here.
import type {
  ClientPaymentCheckoutBody,
  ClientPaymentPortalBody,
  CreateCheckoutBody,
  CreateClientChargeBody,
  UpdateSubscriptionBody,
  VerifyCheckoutBody,
} from '@schemas/billing';

type UrlResponse = ApiResponse & { url?: string };
// ---- Coach subscription (Coach OS plan) ----

export function createCoachCheckout(input: CreateCheckoutBody): Promise<UrlResponse> {
  return api.post('/api/billing/create-checkout', input) as Promise<UrlResponse>;
}

/** Fails with code SUBSCRIPTION_CANCELED when a fresh checkout is required instead. */
export function updateCoachSubscription(input: UpdateSubscriptionBody): Promise<ApiResponse> {
  return api.post('/api/billing/update-subscription', input);
}

/**
 * After Stripe Checkout redirects back (?billing=success&session_id=…): sync the subscription
 * directly rather than waiting for the webhook. Success carries `subscription`.
 */
export function verifyCoachCheckout(
  sessionId: string,
): Promise<ApiResponse & { subscription?: { plan_tier?: string } & Record<string, unknown> }> {
  return api.post('/api/billing/verify-checkout', { session_id: sessionId } satisfies VerifyCheckoutBody);
}

export function openCoachBillingPortal(): Promise<UrlResponse> {
  return api.post('/api/billing/portal') as Promise<UrlResponse>;
}

// ---- Coach → client billing (Stripe Connect) ----

export function startConnectOnboarding(): Promise<UrlResponse> {
  return api.post('/api/billing/connect/onboard') as Promise<UrlResponse>;
}

export function createClientCharge(input: CreateClientChargeBody): Promise<ApiResponse & { payment_id?: string }> {
  return api.post('/api/billing/client-charges', input);
}

export function cancelClientCharge(paymentId: string): Promise<ApiResponse> {
  return api.delete(`/api/billing/client-charges/${encodeURIComponent(paymentId)}`);
}

// ---- Client-side payments ----

export function startClientPaymentCheckout(paymentId: string): Promise<UrlResponse> {
  return api.post('/api/clients/payments/checkout', {
    payment_id: paymentId,
  } satisfies ClientPaymentCheckoutBody) as Promise<UrlResponse>;
}

export function openClientPaymentPortal(paymentId: string): Promise<UrlResponse> {
  return api.post('/api/clients/payments/portal', {
    payment_id: paymentId,
  } satisfies ClientPaymentPortalBody) as Promise<UrlResponse>;
}
